import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { registryServiceURL } from '../../../redux/services/defines';
import { getAuthToken, getRememberMePreference, setAuthToken } from './storage';

export interface AuthUser {
  id: string;
  username: string;
  email: string;
  fullName: string;
  role: 'admin' | 'user';
  isSuperAdmin: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  lastLoginAt?: string;
}

type AuthStatus = 'loading' | 'setup-required' | 'unauthenticated' | 'authenticated';

interface SetupInput {
  username: string;
  email: string;
  fullName: string;
  password: string;
  companyName?: string;
}

interface AuthContextValue {
  status: AuthStatus;
  isLoading: boolean;
  isAuthenticated: boolean;
  needsSetup: boolean;
  user: AuthUser | null;
  login: (identifier: string, password: string, rememberMe?: boolean) => Promise<void>;
  setup: (input: SetupInput) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  rememberMe: boolean;
  setRememberMe: (value: boolean) => void;
}

const AuthContext = createContext<AuthContextValue>({
  status: 'loading',
  isLoading: true,
  isAuthenticated: false,
  needsSetup: false,
  user: null,
  login: async () => {
    throw new Error('Auth context not initialized');
  },
  setup: async () => {
    throw new Error('Auth context not initialized');
  },
  logout: async () => {
    throw new Error('Auth context not initialized');
  },
  refresh: async () => {
    throw new Error('Auth context not initialized');
  },
  rememberMe: false,
  setRememberMe: () => {},
});

const parseJson = async (response: Response) => {
  const text = await response.text();
  if (!text) {
    return null;
  }
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
};

const resolveErrorMessage = (payload: any, fallback: string): string => {
  if (typeof payload?.message === 'string' && payload.message.trim()) {
    return payload.message;
  }
  if (Array.isArray(payload?.message) && payload.message[0]) {
    return String(payload.message[0]);
  }
  return fallback;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<AuthUser | null>(null);
  const [rememberMe, setRememberMe] = useState<boolean>(() => getRememberMePreference());

  const refresh = useCallback(async () => {
    try {
      setStatus('loading');

      const setupResponse = await fetch(`${registryServiceURL}/v1/auth/setup-status`, {
        headers: { 'Content-Type': 'application/json' },
      });
      const setupPayload = await parseJson(setupResponse);
      const needsSetup = Boolean(setupPayload?.needsSetup);

      if (needsSetup) {
        setAuthToken(null);
        setUser(null);
        setStatus('setup-required');
        return;
      }

      const token = getAuthToken();
      if (!token) {
        setUser(null);
        setStatus('unauthenticated');
        return;
      }

      const meResponse = await fetch(`${registryServiceURL}/v1/auth/me`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (!meResponse.ok) {
        setAuthToken(null);
        setUser(null);
        setStatus('unauthenticated');
        return;
      }

      const mePayload = (await parseJson(meResponse)) as AuthUser;
      setUser(mePayload);
      setStatus('authenticated');
    } catch {
      setAuthToken(null);
      setUser(null);
      setStatus('unauthenticated');
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(async (identifier: string, password: string, remember: boolean = rememberMe) => {
    const response = await fetch(`${registryServiceURL}/v1/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ identifier, password, rememberMe: remember }),
    });

    const payload = await parseJson(response);
    if (!response.ok) {
      throw new Error(resolveErrorMessage(payload, 'Login failed'));
    }

    setRememberMe(remember);
    setAuthToken(payload.token, remember);
    setUser(payload.user as AuthUser);
    setStatus('authenticated');
  }, [rememberMe]);

  const setup = useCallback(async (input: SetupInput) => {
    const response = await fetch(`${registryServiceURL}/v1/auth/setup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });

    const payload = await parseJson(response);
    if (!response.ok) {
      throw new Error(resolveErrorMessage(payload, 'Setup failed'));
    }

    setAuthToken(payload.token, true);
    setUser(payload.user as AuthUser);
    setStatus('authenticated');
  }, []);

  const logout = useCallback(async () => {
    const token = getAuthToken();
    if (token) {
      try {
        await fetch(`${registryServiceURL}/v1/auth/logout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
      } catch {
        // Ignore logout API errors and force local logout.
      }
    }

    setAuthToken(null);
    setUser(null);
    setStatus('unauthenticated');
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      isLoading: status === 'loading',
      isAuthenticated: status === 'authenticated',
      needsSetup: status === 'setup-required',
      user,
      login,
      setup,
      logout,
      refresh,
      rememberMe,
      setRememberMe,
    }),
    [status, user, login, setup, logout, refresh, rememberMe],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
